import { type ReactElement, type RefObject, useMemo, useState } from 'react'
import { type DraggableLineRef, ImmutableVector3, toImmutable } from '@modugen/ui-toolkit'
import { Line } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { getTapelineEndTicks } from './utils'

const POSITION_EPSILON = 1e-6

const noRaycast = (): null => null

interface Props {
  start: ImmutableVector3
  end: ImmutableVector3
  color: string
  lineWidth: number
  visible: boolean
  lineRef?: RefObject<DraggableLineRef | null>
}

const TapelineEndTicks = ({
  start,
  end,
  color,
  lineWidth,
  visible,
  lineRef,
}: Props): ReactElement | null => {
  const [liveStart, setLiveStart] = useState<ImmutableVector3 | null>(null)
  const [liveEnd, setLiveEnd] = useState<ImmutableVector3 | null>(null)

  useFrame(() => {
    if (!lineRef) return

    const startMesh = lineRef.current?.startHandleRef.current
    const endMesh = lineRef.current?.endHandleRef.current
    if (!startMesh || !endMesh) {
      setLiveStart(prev => (prev === null ? prev : null))
      setLiveEnd(prev => (prev === null ? prev : null))
      return
    }

    const nextStart = toImmutable(startMesh.position)
    const nextEnd = toImmutable(endMesh.position)
    setLiveStart(prev => (prev && prev.distanceTo(nextStart) < POSITION_EPSILON ? prev : nextStart))
    setLiveEnd(prev => (prev && prev.distanceTo(nextEnd) < POSITION_EPSILON ? prev : nextEnd))
  })

  const resolvedStart = liveStart ?? start
  const resolvedEnd = liveEnd ?? end
  const ticks = useMemo(
    () => getTapelineEndTicks(resolvedStart, resolvedEnd),
    [resolvedStart, resolvedEnd],
  )

  if (!visible || !ticks) return null

  return (
    <group>
      <Line
        points={[ticks.startTick.start.v, ticks.startTick.end.v]}
        color={color}
        lineWidth={lineWidth}
        dashed={false}
        raycast={noRaycast}
      />
      <Line
        points={[ticks.endTick.start.v, ticks.endTick.end.v]}
        color={color}
        lineWidth={lineWidth}
        dashed={false}
        raycast={noRaycast}
      />
    </group>
  )
}

export default TapelineEndTicks
